import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
  Avatar,
  Paper,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { searchBrandsByName } from "../../services/brandfetch";

export interface Company {
  name: string;
  domain: string;
  icon?: string;
  brandId?: string;
}

interface CompanyAutocompleteProps {
  value?: string;
  onChange: (value: Company) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  size?: "small" | "medium";
}

const CompanyAutocomplete: React.FC<CompanyAutocompleteProps> = ({
  value = "",
  onChange,
  label = "Company",
  placeholder = "Search for a company...",
  required = false,
  fullWidth = true,
  disabled = false,
  size = "small",
}) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (value) {
      setInputValue(value);
    }
  }, [value]);

  const fetchCompanies = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const results = await searchBrandsByName(query);
      if (results) {
        const companies: Company[] = results.map((result) => ({
          name: result.name || result.domain,
          domain: result.domain,
          icon: result.icon || undefined,
          brandId: result.brandId,
        }));
        setOptions(companies);
      } else {
        setOptions([]);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCompanies(inputValue);
    }, 200);

    return () => clearTimeout(timer);
  }, [inputValue, fetchCompanies]);

  return (
    <Box sx={{ width: fullWidth ? "100%" : "auto" }}>
      <Autocomplete
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.name || option.domain
        }
        options={options}
        loading={loading}
        filterOptions={(x) => x}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onChange={(_, selectedOption) => {
          if (selectedOption) {
            if (typeof selectedOption === "string") {
              onChange({
                name: selectedOption,
                domain: "",
              });
            } else {
              onChange(selectedOption);
            }
          }
        }}
        freeSolo
        clearOnBlur={false}
        blurOnSelect={false}
        noOptionsText={
          inputValue.length < 2 ? (
            "Type to search..."
          ) : (
            <Box
              sx={{
                p: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <SearchIcon
                sx={{ mb: 1, fontSize: "1.5rem", color: "text.secondary" }}
              />
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Nothing found...
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                Try searching by company website domain for better results.
              </Typography>
            </Box>
          )
        }
        renderOption={(props, option: Company) => (
          <Paper component="li" {...props} elevation={0}>
            <Box sx={{ display: "flex", alignItems: "center", p: 1 }}>
              {option.icon ? (
                <Avatar
                  src={option.icon}
                  alt={option.name}
                  sx={{ width: 24, height: 24, mr: 2 }}
                />
              ) : (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    mr: 2,
                    bgcolor: "grey.200",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {option.name?.charAt(0) || option.domain.charAt(0)}
                </Box>
              )}
              <Box sx={{ ml: 1 }}>
                <Typography variant="body2" component="div">
                  {option.name || option.domain}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.domain}
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            required={required}
            fullWidth={fullWidth}
            disabled={disabled}
            size={size}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{ minWidth: 250 }}
      />
    </Box>
  );
};

export default CompanyAutocomplete;
